import { useMemo, useState } from "react";
import SectionHeading from "@/components/base/SectionHeading";
import { allTopics, categories } from "@/mocks/spotlight";

type TopicSquareProps = {
  query: string;
  setQuery: (query: string) => void;
};

export default function TopicSquare({ query, setQuery }: TopicSquareProps) {
  const [category, setCategory] = useState("全部");

  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return allTopics.filter((topic) => {
      const categoryOk = category === "全部" || topic.category === category;
      const keywordOk =
        !keyword ||
        `${topic.title}${topic.summary}${topic.category}`.toLowerCase().includes(keyword);
      return categoryOk && keywordOk;
    });
  }, [category, query]);

  return (
    <section id="square" className="w-full bg-background-100">
      <div className="mx-auto w-full max-w-[1200px] px-4 md:px-6 py-14 md:py-20">
        <SectionHeading
          eyebrow="Topic Square"
          title="话题广场"
          desc="按分类浏览正在发酵的热门话题，也可以用关键词快速定位。每个话题都汇集了网友的讨论与不同立场的观点。"
        />

        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-1 rounded-lg border border-background-200 bg-background-50 p-1">
            {categories.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setCategory(item)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 font-label text-sm transition-colors ${
                  category === item
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-700 hover:bg-background-100 hover:text-primary-600"
                }`}
              >
                {item}
              </button>
            ))}
          </div>

          <div className="relative w-full lg:w-72">
            <span className="pointer-events-none absolute left-4 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-search-line text-sm"></i>
            </span>
            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="搜索话题关键词"
              aria-label="搜索话题关键词"
              className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-4 font-label text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/40"
            />
          </div>
        </div>

        <p className="mt-5 font-label text-sm text-foreground-500">
          共找到 <span className="font-semibold text-primary-600">{filtered.length}</span> 个话题
        </p>

        {filtered.length === 0 ? (
          <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-search-eye-line text-2xl"></i>
            </span>
            <p className="mt-4 font-heading text-lg font-semibold text-foreground-900">没有找到相关话题</p>
            <p className="mt-1 text-sm text-foreground-500">换个关键词，或切换到其他分类看看。</p>
            <button
              type="button"
              onClick={() => {
                setQuery("");
                setCategory("全部");
              }}
              className="mt-5 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((topic) => (
              <article
                key={topic.id}
                className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
              >
                <div className="flex items-center justify-between">
                  <span className="rounded-full bg-secondary-100 px-3 py-1 font-label text-xs text-secondary-900">
                    {topic.category}
                  </span>
                  <span className="font-label text-xs text-foreground-400">{topic.time}</span>
                </div>
                <h3 className="mt-3 font-heading text-base font-semibold leading-snug text-foreground-950">
                  {topic.title}
                </h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600">{topic.summary}</p>
                <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-4">
                  <div className="flex items-center gap-4 font-label text-xs text-foreground-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-fire-line text-primary-500"></i>
                      {topic.heat}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-chat-1-line"></i>
                      {topic.comments}
                    </span>
                  </div>
                  <a
                    href="#arena"
                    className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap font-label text-xs font-medium text-primary-600 transition-colors hover:text-primary-700"
                  >
                    看观点
                    <i className="ri-arrow-right-up-line"></i>
                  </a>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}